type Timeslot = {
    startTime?: string
    endTime?: string
}

/**
 * Pads a time to HH:mm, so times can be compared as strings.
 * Returns '' for an empty value and null for a value that is not a valid time.
 */
export function normalizeTime(value?: string): string | null {
    if (value === undefined || value === null || `${value}`.trim() === '') {
        return ''
    }

    const match = /^(\d{1,2})[:.](\d{2})(:\d{2})?$/.exec(`${value}`.trim())

    if (!match || parseInt(match[1], 10) > 23 || parseInt(match[2], 10) > 59) {
        return null
    }

    return `${match[1].padStart(2, '0')}:${match[2]}`
}

/**
 * Validates the optional timeslot of an address in the address book.
 * Returns the translation key of the error, or '' when the timeslot is valid.
 */
export function validateTimeslot({startTime, endTime}: Timeslot): '' | 'invalidStartTime' | 'invalidEndTime' | 'endTimeBeforeStartTime' {
    const start = normalizeTime(startTime)
    const end = normalizeTime(endTime)

    if (start === null) return 'invalidStartTime'
    if (end === null) return 'invalidEndTime'
    if (start && end && end <= start) return 'endTimeBeforeStartTime'

    return ''
}

type TimeslotChange = {
    startTimeEnabled?: boolean
    startTime?: string
    endTimeEnabled?: boolean
    endTime?: string
}

/**
 * The timeslot after a bulk change: only the enabled times are replaced, an enabled but empty time clears it.
 * Times are '' rather than undefined, a save without the times keeps the stored timeslot.
 */
export function changeTimeslot(timeslot: Timeslot, {startTimeEnabled, startTime, endTimeEnabled, endTime}: TimeslotChange): Required<Timeslot> {
    return {
        startTime: (startTimeEnabled ? startTime : timeslot.startTime) || '',
        endTime: (endTimeEnabled ? endTime : timeslot.endTime) || ''
    }
}

/**
 * Narrows a timeslot to the overlap with the timeslot of an address book entry. An empty time counts as no limit.
 * Returns null when the address book entry has no timeslot or when both timeslots don't overlap, the timeslot then stays as it is.
 */
export function narrowTimeslot(timeslot: Timeslot, limit: Timeslot): Required<Timeslot> | null {
    const limitStart = normalizeTime(limit.startTime)
    const limitEnd = normalizeTime(limit.endTime)

    if (!limitStart && !limitEnd) {
        return null
    }

    const start = normalizeTime(timeslot.startTime) || '00:00'
    const end = normalizeTime(timeslot.endTime) || '23:59'

    const startTime = limitStart && limitStart > start ? limitStart : start
    const endTime = limitEnd && limitEnd < end ? limitEnd : end

    if (endTime <= startTime) {
        return null
    }

    return {startTime, endTime}
}
